import React from "react";
import { PlatformBrandLogo } from "@/components/branding/platform-brand-logo";
import { DownloadIcon } from "lucide-react";
import LoadingSpinner from "../../loading-spinner";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../../ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { PdfViewer } from "./PdfViewer";
import { SpreadsheetViewer } from "./SpreadsheetViewer";
import { usePdfDocument } from "../hooks/usePdfDocument";
import { useSpreadsheetMeta } from "@/api/spreadsheet";
import "./style/PdfModal.css";

// Props interface for PdfModal (modal wrapper)
interface PdfModalProps {
  // New PdfViewer props
  document?: string;
  pageNumber?: number;
  textToHighlight?: string;
  comment?: string;
  fullPage?: boolean;
  documentId?: string;
  readOnly?: boolean; // Disable Q&A and highlighting features

  // Citation linking props
  target?: {
    instanceId: string; // Required: Project/Portfolio in which the document is being loaded
    groupId?: string; // Optional: Answer Group ID for question/answer selection
    citationId?: string; // Optional: Citation ID to scroll to and highlight
  };

  // Legacy FileViewer props for compatibility
  doc?: {
    id: string;
    filename: string;
    category_drilldown?: string;
  };
  onClose?: () => void;
  open?: boolean;
  // Fired once the viewer has actually scrolled to the target citation, so the
  // opener (e.g. documents-table) can retire the deep-link URL only after the
  // evidence destination is fulfilled — not optimistically on open.
  onTargetReached?: () => void;
}

// Full-screen PDF viewer component that behaves like the old FileViewer
export const PdfModal: React.FC<PdfModalProps> = (props) => {
  const {
    doc,
    onClose,
    document: documentId,
    open = true,
    target,
    ...otherProps
  } = props;

  // Stabilize the document object to prevent unnecessary re-renders
  const docToUse = React.useMemo(() => {
    return (
      doc || {
        id: documentId || props.documentId || "",
        filename: "document.pdf",
        category_drilldown: undefined,
      }
    );
  }, [doc, documentId, props.documentId]);

  // Extract stable values
  const documentIdStable = docToUse.id;
  const filenameStable = docToUse.filename;

  // Documents with natively-parsed spreadsheet data render in the
  // SpreadsheetViewer; everything else takes the existing PDF path. The
  // probe must resolve before the PDF fetch so XLSX files don't trigger a
  // needless (or impossible) server-side PDF conversion.
  const { data: spreadsheetMeta, isLoading: spreadsheetMetaLoading } =
    useSpreadsheetMeta(documentIdStable);
  const isSpreadsheet = (spreadsheetMeta?.sheets?.length ?? 0) > 0;

  // Use the custom hook for document loading
  const { documentData, isLoading, hasError, dontRender, handleDownload } =
    usePdfDocument({
      documentId: documentIdStable,
      filename: filenameStable,
      onClose,
      enabled: !spreadsheetMetaLoading && !isSpreadsheet,
    });

  // Handle ESC key to close viewer (like old FileViewer)
  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    },
    [onClose],
  );

  React.useEffect(() => {
    if (dontRender) return;

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, dontRender]);

  if (!isSpreadsheet && (hasError || dontRender)) {
    return null;
  }

  const contentReady = isSpreadsheet
    ? !spreadsheetMetaLoading
    : !isLoading && !!documentData;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent
        className="h-full max-h-[100vh] w-full max-w-[100vw] bg-black/70 p-0"
        overlayClassName="py-0"
        showCloseButton={false}
        closeCallback={onClose}
      >
        <VisuallyHidden>
          <DialogTitle>Document viewer - {docToUse.filename}</DialogTitle>
          <DialogDescription>
            Full-screen document viewer for {docToUse.filename}. Press Escape
            key to close.
          </DialogDescription>
        </VisuallyHidden>
        {/* Loading spinner */}
        {!contentReady && (
          <div className="absolute inset-0 z-[2000]">
            <LoadingSpinner delay={false} />
          </div>
        )}

        {/* Viewer with proper layout (spreadsheet grid or PDF) */}
        {contentReady && (
          <div className="flex h-full w-full flex-col overflow-hidden">
            {/* Fixed header - 60px height */}
            <div className="z-[2000] flex h-[60px] w-full flex-shrink-0 items-center gap-x-2 border-b border-gray-300 bg-white pl-2 pt-1 md:pl-16">
              <div className="absolute left-0 top-0 hidden h-[60px] w-[50px] items-center justify-center p-1 md:flex">
                <PlatformBrandLogo variant="square" />
              </div>
              <div>
                <Button
                  onClick={handleDownload}
                  size="icon"
                  className="h-9 w-9"
                  variant="outline"
                >
                  <DownloadIcon size="18" />
                </Button>
              </div>
              <div className="flex flex-col justify-start text-left">
                <span className="line-clamp-1 text-xs text-muted-foreground">
                  {docToUse.category_drilldown}
                </span>
                <span className="text-md line-clamp-1 font-semibold">
                  {docToUse.filename}
                </span>
              </div>
              <div className="ml-auto flex items-center gap-x-4 pr-2">
                {props.readOnly ? (
                  <div className="rounded-md border-yellow-200 bg-yellow-50 px-4 py-2 text-sm text-yellow-800">
                    <div className="flex items-center justify-center gap-2">
                      <span className="font-medium">📖 Read-only mode:</span>
                      <span>Q&A and highlighting features are disabled</span>
                    </div>
                  </div>
                ) : isSpreadsheet ? null : (
                  <span className="text-xs text-muted-foreground">
                    Hold ⌥ Option (Alt) + click and drag to create area
                    highlights
                  </span>
                )}
                {onClose && (
                  <Button variant="outline" onClick={onClose}>
                    Close
                  </Button>
                )}
              </div>
            </div>

            {/* Content area - takes remaining height */}
            <div className="min-h-0 flex-1 overflow-hidden">
              {isSpreadsheet ? (
                <SpreadsheetViewer
                  documentId={documentIdStable}
                  target={target}
                />
              ) : (
                <PdfViewer
                  document={documentData}
                  pageNumber={otherProps.pageNumber}
                  textToHighlight={otherProps.textToHighlight}
                  comment={otherProps.comment}
                  documentId={documentIdStable}
                  target={target}
                  readOnly={props.readOnly}
                  onTargetReached={props.onTargetReached}
                />
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PdfModal;
